import * as THREE from 'three'

export type WristStyle = 'bangle' | 'bangles' | 'watch'

export interface ProceduralWristOptions {
  style: WristStyle
  metal: number | string
  accent: number | string
  /** Inner opening across the wrist (cm). */
  innerWidth: number
}

export const WRIST_DEFAULTS: ProceduralWristOptions = {
  style: 'watch',
  metal: 0xd4a94a,
  accent: 0x14161c,
  innerWidth: 5.8,
}

function metal(c: number | string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(c), metalness: 1, roughness: 0.25, envMapIntensity: 1.1 })
}
function matte(c: number | string, roughness = 0.5): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(c), metalness: 0.1, roughness, envMapIntensity: 0.6 })
}

/**
 * Wrist wearables in cm. Frame: +Z along the forearm toward the ELBOW, +Y dorsal (back of hand),
 * origin at the solver's seat point. The wrist cross-section is elliptical: wider (x) than deep (y).
 */
export function createProceduralWrist(options: Partial<ProceduralWristOptions> = {}): THREE.Group {
  const o = { ...WRIST_DEFAULTS, ...options }
  const g = new THREE.Group()
  g.name = `procedural-wrist-${o.style}`
  const rx = o.innerWidth / 2
  const ry = rx * 0.72

  const ellipticalRing = (tube: number, mat: THREE.Material, z = 0): THREE.Mesh => {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1, tube, 12, 48), mat)
    ring.scale.set(rx + tube, ry + tube, 1)
    ring.position.z = z
    return ring
  }

  if (o.style === 'bangle') {
    const b = ellipticalRing(0.28, metal(o.metal))
    b.name = 'bangle'
    g.add(b)
  } else if (o.style === 'bangles') {
    for (let i = 0; i < 3; i++) {
      const b = ellipticalRing(0.14, metal(i === 1 ? o.accent : o.metal), i * 0.55 - 0.55)
      b.name = `bangle_${i}`
      g.add(b)
    }
  } else {
    // watch: elliptical strap + case/dial on the dorsal side (+Y)
    const strap = ellipticalRing(0.35, matte(o.accent, 0.7))
    strap.name = 'strap'
    const caseM = new THREE.Mesh(new THREE.CylinderGeometry(1.85, 1.85, 0.75, 28), metal(o.metal))
    caseM.name = 'case'
    caseM.position.y = ry + 0.35
    const dial = new THREE.Mesh(new THREE.CylinderGeometry(1.55, 1.55, 0.1, 28), matte(0x0d0e12, 0.25))
    dial.name = 'dial'
    dial.position.y = ry + 0.75
    const hourHand = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.04, 0.85), metal(0xdddddd))
    hourHand.position.set(0, ry + 0.82, -0.3)
    hourHand.name = 'hand_hour'
    const minHand = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.04, 1.25), metal(0xdddddd))
    minHand.rotation.y = Math.PI / 3
    minHand.position.y = ry + 0.86
    minHand.name = 'hand_min'
    const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.22, 10), metal(o.metal))
    crown.rotation.z = Math.PI / 2
    crown.position.set(1.95, ry + 0.35, 0)
    crown.name = 'crown'
    g.add(strap, caseM, dial, hourHand, minHand, crown)
  }
  return g
}
